import { observer } from "mobx-react-lite"
import type { ReactNode } from "react"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface ActivityListItem {
  id: string
  icon: ReactNode
  title: string
  description?: string
  date: string
}

interface ActivityListCardProps {
  title: string
  action?: ReactNode
  activities: ActivityListItem[]
  theme?: ThemeConfigType
  className?: string
}

export const ActivityListCard = observer(
  ({ title, action, activities, theme, className }: ActivityListCardProps) => {
    // Always call useTheme hook unconditionally
    const themeClasses = useTheme(theme || getDefaultTheme())
    const isDefaultTheme = !theme

    return (
      <Card
        className={cn(
          "w-full",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader>
          <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>{title}</CardTitle>
          {action && <CardAction>{action}</CardAction>}
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "space-y-4",
              theme?.spacing === "compact" && "space-y-2",
              theme?.spacing === "comfortable" && "space-y-6",
            )}
          >
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center justify-between gap-4 pb-4 border-b last:border-0 last:pb-0"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="shrink-0 w-8 h-8 flex items-center justify-center">
                    {activity.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        "text-sm font-medium truncate",
                        !isDefaultTheme && themeClasses.typography,
                      )}
                    >
                      {activity.title}
                    </p>
                    {activity.description && (
                      <p
                        className={cn(
                          "text-xs text-muted-foreground truncate mt-0.5",
                          !isDefaultTheme && themeClasses.typography,
                        )}
                      >
                        {activity.description}
                      </p>
                    )}
                  </div>
                </div>
                <time
                  className={cn(
                    "text-sm text-muted-foreground whitespace-nowrap",
                    !isDefaultTheme && themeClasses.typography,
                  )}
                >
                  {activity.date}
                </time>
              </div>
            ))}
            {activities.length === 0 && (
              <p
                className={cn(
                  "text-sm text-muted-foreground text-center py-8",
                  !isDefaultTheme && themeClasses.typography,
                )}
              >
                No activities to display
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    )
  },
)

ActivityListCard.displayName = "ActivityListCard"
